import React, { useState, useEffect } from "react";
import axios from "axios";
import { Check, Loader2, ArrowRight, X, Flame } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess({ sessionId, onContinue, onRetry }) {
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [packageName, setPackageName] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async (attempts) => {
      if (cancelled) return;
      if (attempts >= 6) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`, { withCredentials: true });
        if (cancelled) return;
        if (data.package_name) setPackageName(data.package_name);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch {
        setTimeout(() => poll(attempts + 1), 2000);
      }
    };

    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-black text-white font-manrope flex flex-col items-center justify-center gap-8 px-6" data-testid="payment-success-page">
      <img
        src="/assets/ssa-logo-white.png"
        alt="ForgedByStokes"
        className="h-16 w-auto"
      />
      <div className="max-w-md w-full border border-neutral-800 bg-neutral-950/40 p-10 text-center space-y-6">
        {status === "checking" && (
          <div className="space-y-5" data-testid="payment-checking">
            <Loader2 className="w-10 h-10 animate-spin text-neutral-500 mx-auto" />
            <h1 className="font-bebas text-3xl uppercase tracking-wide">Confirming Your Enrollment</h1>
            <p className="text-xs text-neutral-400 uppercase tracking-widest leading-relaxed">
              Hold tight while we verify your payment with Stripe...
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6" data-testid="payment-success-state">
            <div className="w-16 h-16 border-2 border-white rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-white" />
            </div>
            <div className="space-y-2">
              <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold flex items-center justify-center gap-1.5">
                <Flame className="w-3.5 h-3.5" /> Welcome To The Family
              </span>
              <h1 className="font-bebas text-4xl uppercase tracking-wide leading-none">You're In.</h1>
            </div>
            <p className="text-sm text-neutral-400 leading-relaxed">
              {packageName ? <>Your <span className="text-white font-semibold">{packageName}</span> is secured.</> : "Your transformation is secured."} The work begins now. Stokes will guide your mind, body, and spirit.
            </p>
            <blockquote className="italic text-xs text-neutral-500 border-l-2 border-white pl-4 text-left">
              "For physical training is of some value, but godliness has value for all things." — 1 Timothy 4:8
            </blockquote>
            <button
              onClick={onContinue}
              className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all flex items-center justify-center gap-2"
              data-testid="payment-continue-dashboard"
            >
              <span>Enter Your Temple Portal</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {(status === "failed" || status === "timeout") && (
          <div className="space-y-5" data-testid="payment-failed-state">
            <div className="w-16 h-16 border-2 border-neutral-600 rounded-full flex items-center justify-center mx-auto">
              <X className="w-8 h-8 text-neutral-400" />
            </div>
            <h1 className="font-bebas text-3xl uppercase tracking-wide">
              {status === "timeout" ? "Still Processing" : "Payment Incomplete"}
            </h1>
            <p className="text-xs text-neutral-400 uppercase tracking-widest leading-relaxed">
              {status === "timeout"
                ? "Your payment is taking longer than expected. Check your dashboard shortly or contact support."
                : "Your checkout session expired or was cancelled. You can try again."}
            </p>
            <button
              onClick={onRetry}
              className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all"
              data-testid="payment-retry-button"
            >
              Return Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
